import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Previewer } from "pagedjs";
import { debugLog, scrollToAnchor } from "../utils/index";
import { createPaginatedToc } from "../utils/createToc";
import createHamburgerMenu from "../utils/hamburgerMenu";
import injectHamburgerMenuStyles from "../utils/hamburgerMenuStyles";

function Paginator({ children, disableToc = false, manuals = [], currentManualId = null, currentSiteId = null }) {
  useEffect(() => {
    if (typeof window === "undefined") {
      console.warn("Previewer did not run - SSR environment detected");
      return;
    }

    // Hamburger control ref for cleanup
    let hamburgerControl = null;
    // Headings extracted in beforeParsed, used after preview() completes
    let storedHeadings = null;
    // Extra cleanup callbacks registered asynchronously (e.g. print listeners)
    const effectCleanupFns = [];

    const previewer = new Previewer();
    console.log("Created previewer");

    // Add the beforeParsed hook for TOC generation only (no hamburger here —
    // hamburger is created after preview() so it lands outside PagedJS's
    // restructured page elements, keeping position:fixed viewport-relative)
    previewer.chunker.hooks.beforeParsed.register(function(content) {
      if (disableToc) {
        debugLog(1,"disableToc, skipping TOC");
        return content;
      }
      console.log("beforeParsed hook called with content:", !!content);

      if (content) {
        const coverElement = content.querySelector(
          ".flex.flex-col.justify-center.h-page"
        );
        const manualElement = content.querySelector(".Manual");

        let tocElement = content.querySelector("#my-toc-content");
        if (!tocElement) {
          console.log("Creating TOC element");
          tocElement = document.createElement("div");
          tocElement.id = "my-toc-content";
          tocElement.classList.add("toc-container");
          tocElement.classList.add("page-break");

          if (manualElement && manualElement.parentNode) {
            manualElement.parentNode.insertBefore(tocElement, manualElement);
          } else if (coverElement && coverElement.parentNode) {
            if (coverElement.nextSibling) {
              coverElement.parentNode.insertBefore(
                tocElement,
                coverElement.nextSibling
              );
            } else {
              coverElement.parentNode.appendChild(tocElement);
            }
          } else {
            content.appendChild(tocElement);
          }
        }

        try {
          const tocResult = createPaginatedToc({
            content,
            tocElement: "#my-toc-content",
            titleElements: [".title-chapter", ".title-topic"],
            removeFallbackOnlyIfHeadingsFound: true,
            singleLineEntries: true,
          });

          if (tocResult) {
            console.log(
              `TOC created in paginated context with ${tocResult.headingCount} headings`
            );
            if (tocResult.tocElement) {
              tocResult.tocElement.classList.add("paginated-toc");
            }
            // Store headings for hamburger creation after preview() completes
            if (tocResult.headings && tocResult.headings.length > 0) {
              storedHeadings = tocResult.headings;
            }
          } else {
            console.log("No TOC was created (no headings found or container missing)");
          }
        } catch (error) {
          console.error("Error in TOC creation:", error);
        }
      }

      return content;
    });

    // Browser detection
    const isSafari =
      !!navigator.userAgent && /Safari/.test(navigator.userAgent);
    const isChrome =
      !!navigator.userAgent && /Chrome/.test(navigator.userAgent);

    if (isSafari && !isChrome) {
      console.warn("Skipping preview on Safari to avoid duplication issue.");
      return;
    }
    console.log("Running preview");

    previewer
      .preview(null, ["/preview.css", "/paginator.css"])
      .then(flow => {
        console.log("Preview completed successfully");
        debugLog("preview rendered, total pages:", flow.total, { flow });
        scrollToAnchor();

        // Skip hamburger entirely when running in print context
        // (headless Chrome PDF generation sets matchMedia('print') = true).
        if (window.matchMedia && window.matchMedia("print").matches) {
          console.log("Print context detected — skipping hamburger menu");
          return;
        }

        // Create hamburger NOW — after PagedJS has restructured the DOM.
        // Appending to document.body here ensures the element sits outside
        // any PagedJS page-box, so position:fixed is viewport-relative.
        const buildHamburger = () => {
          if (storedHeadings && storedHeadings.length > 0) {
            try {
              injectHamburgerMenuStyles();
              hamburgerControl = createHamburgerMenu({
                headings: storedHeadings,
                container: document.body,
                manuals,
                currentManualId,
                currentSiteId,
              });
              if (hamburgerControl) {
                console.log(
                  `Hamburger menu created with ${storedHeadings.length} headings`
                );
              }
            } catch (hamburgerError) {
              console.error("Error creating hamburger menu:", hamburgerError);
            }
          }
        };

        buildHamburger();

        // Remove before print, restore after — covers the case where the user
        // triggers browser print from the screen view (Ctrl+P / File > Print).
        // Note: these listeners are NOT removed during beforeprint so that
        // afterprint can still fire and rebuild the menu.
        const onBeforePrint = () => {
          if (hamburgerControl && hamburgerControl.destroy) {
            hamburgerControl.destroy();
          }
          hamburgerControl = null;
          const orphan = document.getElementById("integrated-hamburger-menu");
          if (orphan) orphan.remove();
        };
        const onAfterPrint = () => {
          buildHamburger();
        };
        window.addEventListener("beforeprint", onBeforePrint);
        window.addEventListener("afterprint", onAfterPrint);

        // Track listeners at effect scope so the cleanup return fn can remove them
        // regardless of whether hamburgerControl is null at that point.
        effectCleanupFns.push(() => {
          window.removeEventListener("beforeprint", onBeforePrint);
          window.removeEventListener("afterprint", onAfterPrint);
        });
      })
      .catch(err => {
        console.error("Paged.js preview error:", err);
      });

    // Cleanup — runs when the component unmounts (Gatsby SPA navigation).
    // Removes hamburger DOM node and restores global styles that PagedJS
    // injects onto <html>/<body>, which would otherwise bleed into other pages.
    return () => {
      // Run any async-registered cleanup (e.g. print event listeners)
      effectCleanupFns.forEach(fn => fn());

      // Remove hamburger menu
      if (hamburgerControl) {
        hamburgerControl.destroy();
      }
      const orphan = document.getElementById("integrated-hamburger-menu");
      if (orphan) orphan.remove();
      document.body.classList.remove("hamburger-menu-open");

      // Restore html/body styles set by PagedJS
      const styleProps = ["overflow", "width", "height", "transform", "position"];
      styleProps.forEach(prop => {
        document.body.style[prop] = "";
        document.documentElement.style[prop] = "";
      });

      // Remove PagedJS class from <html>
      document.documentElement.classList.remove("pagedjs_paginated");
    };
  }, []);

  return <>{children}</>;
}

Paginator.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Paginator;
